import { PageHero, CTASection } from "@/components/Bits";
import { FloatingQuote } from "@/components/FloatingQuote";
import { SectionLabel } from "@/components/SectionLabel";
import { Reveal } from "@/components/Reveal";
import { YouTubeEmbed } from "@/components/YouTubeEmbed";
import { CheckCircle2, Zap, Layers, Award, BookOpen, Compass, Sparkles, FlaskConical, Target, Route, Volume2, EyeOff, Timer, Users, Gauge, Ear, XCircle, ArrowRight } from "lucide-react";

// Real Concept-Lab photographs uploaded by the client (Jan 2026)
const B = "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/";
const LR_PHOTOS = {
  distillation: B + "mshg4mb2_Screenshot%202026-01-31%20155637.png", // 2 students at distillation apparatus
  fumeHood:     B + "25b7tp2q_Screenshot%202026-01-31%20155616.png", // student inside fume-hood cabinet
  microscope:   B + "2cztet2t_Screenshot%202026-01-31%20155353.png", // 2 students at microscope + notebook
  pipette:      B + "pxzaux2x_Screenshot%202026-01-31%20153409.png", // 2 students at chemistry bench
};

const FOUNDATIONS = [
  {
    icon: Zap,
    tag: "Mihaly Csikszentmihalyi",
    t: "The State of Flow",
    d: "A condition in which learners become completely absorbed in a meaningful, appropriately challenging activity — experiencing deep concentration, enjoyment and a strong desire to continue learning. PBL-TDLE engineers this state daily.",
  },
  {
    icon: Layers,
    tag: "Lev Vygotsky",
    t: "The Zone of Proximal Development",
    d: "Students learn best on tasks slightly beyond what they can do alone — but achievable with guidance. Teachers demonstrate, ask, and support; peers collaborate; then support is gradually removed to build independence.",
  },
  {
    icon: Award,
    tag: "Malcolm Gladwell",
    t: "Excellence Through Hard Work",
    d: "Exceptional ability grows through deliberate, repeated practice. PBL-TDLE turns this into a system — continuous investigation, formative assessment, and progressively harder problems that transform effort into ability.",
  },
];

const ENGAGEMENT_BULLETS = [
  "Real-life and concept-based problems that stimulate curiosity",
  "Teacher demonstrations that make abstract concepts visible",
  "Hands-on experiments performed by every student",
  "Guided questioning, discussion and collaborative learning",
  "Continuous formative assessments with timely feedback",
  "Progressive problem solving — curriculum → Olympiad → research",
  "Opportunities to explain, present, defend and improve solutions",
  "Regular application of concepts in unfamiliar situations",
];

const CHALK_DRAWBACKS = [
  {
    icon: Volume2,
    hex: "#E14C7A",
    t: "It's a one-way flow",
    d: "The teacher speaks and students listen. Learning appears to be happening, but the teacher has no reliable way of knowing what each student actually understood during the lesson.",
  },
  {
    icon: EyeOff,
    hex: "#2E6BB0",
    t: "Words in place of visuals",
    d: "Abstract ideas are described rather than shown. Students are left to imagine phenomena — pendulums, reactions, fields — that could have been experienced first-hand in a laboratory.",
  },
  {
    icon: Sparkles,
    hex: "#F0A831",
    t: "Familiarity, not mastery",
    d: "Students can recite definitions and reproduce formulae, yet cannot apply the same concept confidently to an unfamiliar or higher-order problem.",
  },
  {
    icon: Users,
    hex: "#1E9E8A",
    t: "One pace for every mind",
    d: "The whole class moves at the same speed regardless of what each learner needs. Some race ahead, others fall behind, but the lecture continues on schedule.",
  },
  {
    icon: Timer,
    hex: "#8B5CF6",
    t: "Attention fades in minutes",
    d: "The classroom rewards silence over thinking. Focus drifts, and by the middle of the lesson many minds are no longer with the teacher.",
  },
  {
    icon: Gauge,
    hex: "#E14C7A",
    t: "Feedback arrives too late",
    d: "Gaps surface only in the next test — weeks after the concept was 'completed' in the syllabus. By then, the class has already moved on to the next topic.",
  },
  {
    icon: Ear,
    hex: "#2E6BB0",
    t: "Skills can't be listened into",
    d: "Observing, measuring, questioning, verifying and analysing must be practised. Not one of these scientific skills can be developed by hearing about them.",
  },
  {
    icon: XCircle,
    hex: "#F0A831",
    t: "Learning becomes invisible",
    d: "A completed lecture is not evidence of a developed concept. Content delivery is visible; real learning happens quietly inside each student's mind — or doesn't happen at all.",
  },
];


const TEXTBOOK_IDEAS = [
  {
    icon: BookOpen,
    hex: "#2E6BB0",
    t: "Foundation, not the finish line",
    d: "Textbooks are the foundation of learning — but not the complete learning process. Their true purpose is to introduce the concepts students must ultimately master and apply.",
  },
  {
    icon: Sparkles,
    hex: "#F0A831",
    t: "Familiarity is not mastery",
    d: "Reading a chapter or hearing a lecture makes students familiar with a concept. Familiarity should never be mistaken for mastery — knowing a concept is not the same as being able to apply it.",
  },
  {
    icon: Target,
    hex: "#E14C7A",
    t: "The real purpose is application",
    d: "JEE, NEET, Olympiads and international entrance exams don't test recall — they test whether students can apply concepts to unfamiliar problems. Concepts must therefore be developed, not merely taught.",
  },
  {
    icon: FlaskConical,
    hex: "#1E9E8A",
    t: "Concepts are developed, not delivered",
    d: "Just as practical skills can't be developed by listening, conceptual understanding can't be built through lectures alone. Students must investigate, observe, verify, analyse and apply — repeatedly — before a concept becomes permanent knowledge.",
  },
  {
    icon: Layers,
    hex: "#8B5CF6",
    t: "Higher-order problems demand more than exercises",
    d: "Textbook exercises reinforce single concepts. Real examinations demand the integration of many concepts within one problem — which requires systematic development, extensive experimentation and progressively challenging practice.",
  },
  {
    icon: Route,
    hex: "#F0A831",
    t: "Textbook as a concept roadmap",
    d: "In PBL-TDLE every chapter becomes a sequence of demonstrations, investigations, experiments, discussions and progressively harder problems. Every concept is experienced, verified, understood and mastered through application.",
  },
];

export default function LearningRedefined() {
  return (
    <div data-testid="learning-redefined-page">
      <FloatingQuote />
      <PageHero
        n="B"
        label="Learning Redefined"
        lines={["Engage to Learn.", "Learn to Innovate."]}
        intro="Learning is not determined by talent alone. It grows when every student is actively engaged, appropriately challenged and consistently encouraged to think, investigate and solve problems."
      />

      {/* ★ The Core Problem — highlighted callout */}
      <section className="border-t border-white/10" data-testid="lr-core-problem">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-24">
          <Reveal>
            <div className="rounded-3xl border-2 dk-panel relative overflow-hidden p-10 md:p-16" style={{ borderColor: "rgba(240,161,60,0.5)" }}>
              <div className="absolute -top-20 -left-20 w-64 h-64 rounded-full blur-3xl opacity-20" style={{ background: "#F0A13C" }} />
              <div className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full blur-3xl opacity-20" style={{ background: "#2AA7D4" }} />
              <div className="relative">
                <div className="flex items-center gap-3">
                  <span className="text-c-amber text-xl leading-none animate-pulse">✦</span>
                  <span className="font-mono-label text-[11px] tracking-label uppercase text-c-amber">The Most Important Drawback of Traditional Learning</span>
                </div>
                <blockquote className="font-display text-2xl md:text-4xl lg:text-[2.75rem] dk-text leading-tight tracking-tight mt-8 max-w-5xl">
                  “Students do not produce their own answers during examinations. They generally <span className="text-c-amber">reproduce answers prepared beforehand</span> by a teacher, textbook writer or subject expert — testing memorisation rather than understanding, independent thinking or the ability to apply concepts.”
                </blockquote>
                <div className="mt-10 pt-8 border-t dk-border grid md:grid-cols-12 gap-8 items-center">
                  <p className="md:col-span-8 dk-text-soft text-base md:text-lg leading-relaxed">
                    For problem-solving, research and innovation, every student must learn and develop the skill of <span className="dk-text font-medium">creating their own solutions to unfamiliar situations</span>. This must not be an occasional exercise — it should be <span className="text-c-amber font-medium">the curriculum itself, and the daily routine of teaching</span>.
                  </p>
                  <div className="md:col-span-4 md:text-right">
                    <span className="inline-block font-mono-label text-[10px] tracking-label uppercase dk-text-muted border dk-border rounded-full px-4 py-2">
                      This is what PBL-TDLE is built to solve
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* A · From Traditional Learning to PBL-TDLE */}
      <section className="border-t border-white/10" data-testid="lr-traditional-to-pbl">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="A">From Traditional Learning to PBL-TDLE</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            Ten limitations of traditional learning — and how <span className="text-accent">PBL-TDLE solves each one</span>.
          </h2>
          <p className="text-white/70 leading-relaxed mt-6 max-w-3xl">
            Traditional learning generally focuses on content delivery, memorisation and syllabus completion through chalk-and-talk. The PBL-TDLE Framework transforms it into active, measurable and application-oriented learning in which every student develops, verifies, masters and applies concepts independently to solve unfamiliar situations (problems).
          </p>

          <div className="mt-14 space-y-4">
            {[
              { l: "Students do not produce their own answers during examinations. They generally reproduce answers prepared beforehand by a teacher, textbook writer or subject expert — testing memorisation rather than understanding, independent thinking or the ability to apply concepts.", s: "PBL-TDLE enables students to develop concepts and apply them independently to unfamiliar questions. Students construct their own answers, explain their reasoning and defend their solutions. Assessment therefore tests genuine understanding, application, analysis and problem-solving, rather than reproduction alone." },
              { l: "Traditional assessment predominantly tests a single skill: memorisation. However, success in research, engineering, medicine and other professional fields requires several abilities — particularly concept development and concept application.", s: "PBL-TDLE develops a broad range of essential abilities, including conceptual understanding, application, scientific thinking, critical thinking, analytical reasoning, experimentation, data interpretation, communication, creativity and problem-solving." },
              { l: "Content is prioritised over concepts, and teaching is measured by content delivery and syllabus completion. STEM subjects are often presented mainly as facts and information.", s: "PBL-TDLE recognises that STEM is fundamentally based on concepts. It identifies the essential concepts within every unit and measures progress through their development, verification, mastery and application, rather than syllabus completion alone." },
              { l: "Chalk-and-talk depends entirely on listening and continuous attention. Only attentive students may gain facts, information and familiarity, while student engagement is often extremely poor or completely absent.", s: "Teaching by Demonstrations and Learning by Experiments involves every student through observation, experimentation, investigation, discussion, presentation and problem-solving — ensuring active and inclusive engagement." },
              { l: "Abstract concepts are left to students' imagination. Chalk-and-talk may create familiarity with definitions, formulas and explanations, but familiarity does not develop concepts or ensure mastery.", s: "Apparatus, models, demonstrations and experiments make abstract concepts visible and understandable. Students develop concepts by observing, investigating, analysing evidence, verifying results and drawing conclusions." },
              { l: "Learning remains uncertain because there is no reliable verification or evidence of what each student has understood. Physical presence, note-taking and silence do not prove attention, understanding or learning.", s: "Experiments, recorded observations, Formative Concept Assessments, presentations, notebook work and problem-solving provide visible evidence of learning. Immediate feedback and timely correction make learning measurable, verifiable and accountable." },
              { l: "Application of concepts is not systematically included, resulting in almost no meaningful problem-solving. Without concept development, students cannot apply their knowledge confidently to unfamiliar situations.", s: "PBL-TDLE follows the essential progression: Concept Development → Concept Mastery → Application → Problem-Solving. Its multi-level framework develops students from curriculum-level application to advanced, competitive, research-oriented and real-world problem-solving." },
              { l: "Separate preparation is required for school, board and entrance examinations. Students often depend on external tuition or coaching for JEE, NEET and competitive examinations.", s: "One integrated system of concept development and multi-level problem-solving prepares students simultaneously for the school curriculum, board examinations, JEE, NEET, international competitions and Olympiads." },
              { l: "Learning is highly dependent on individual teachers, lesson planning and textbook content. The quality and continuity of learning may vary, while successful teaching is often judged only by syllabus completion.", s: "Structured concept maps, demonstrations, experiments, problems, assessments and the CoPE-App guide teachers systematically through every unit. Textbook concepts are extended to real-life situations, interdisciplinary applications, advanced problems and research questions." },
              { l: "Students are rarely prepared to identify real problems, develop solutions or contribute to innovation. Consequently, there is almost no contribution to major scientific discoveries, inventions, marketable solutions or patents.", s: "PBL-TDLE develops scientific thinking, creativity, research aptitude, innovation and entrepreneurship. Students progress from identifying problems to creating useful solutions, discoveries, inventions and potentially patentable innovations — while understanding intellectual property protection and commercial application." },
            ].map((row, i) => (
              <Reveal key={i} delay={(i % 3) * 0.05}>
                <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden" data-testid={`lr-comparison-row-${i + 1}`}>
                  <div className="bg-[#070d18] p-6 md:p-8">
                    <div className="flex items-center gap-3">
                      <span className="font-display text-3xl text-white/25 leading-none">{String(i + 1).padStart(2, "0")}</span>
                      <span className="font-mono-label text-[10px] tracking-label uppercase text-white/45">Limitation of Traditional Learning</span>
                    </div>
                    <p className="text-white/65 text-sm leading-relaxed mt-4">{row.l}</p>
                  </div>
                  <div className="dk-panel p-6 md:p-8">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-c-teal shrink-0" />
                      <span className="font-mono-label text-[10px] tracking-label uppercase text-c-teal">Solution · PBL-TDLE Framework</span>
                    </div>
                    <p className="dk-text-soft text-sm leading-relaxed mt-4">{row.s}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* B · The Essential Transformation */}
      <section className="border-t border-white/10" data-testid="lr-essential-transformation">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="B">The Essential Transformation</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            From memorised reproduction to <span className="text-accent">original answers</span>.
          </h2>
          <p className="text-white/70 leading-relaxed mt-6 max-w-3xl">
            Students do not produce their own answers during examinations. They generally reproduce answers prepared beforehand by a teacher, textbook writer or subject expert. Such examinations mainly test the ability to memorise and reproduce information, rather than the student's actual understanding, independent thinking or ability to apply concepts.
          </p>

          <div className="mt-14 space-y-6">
            <Reveal>
              <div className="rounded-2xl border border-white/10 bg-[#070d18] p-7 md:p-9" data-testid="transformation-traditional">
                <div className="font-mono-label text-[11px] tracking-label uppercase text-white/45">Traditional Learning</div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-5">
                  {["Prepared Answers", "Memorisation", "Reproduction", "Examination"].map((s, i, arr) => (
                    <span key={s} className="inline-flex items-center gap-3">
                      <span className="font-display text-lg md:text-xl text-white/55 tracking-tight">{s}</span>
                      {i < arr.length - 1 && <ArrowRight className="w-4 h-4 text-white/30" />}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="rounded-2xl border-2 dk-panel p-7 md:p-9 relative overflow-hidden" style={{ borderColor: "rgba(42,167,212,0.5)" }} data-testid="transformation-pbltdle">
                <div className="absolute -top-14 -right-14 w-44 h-44 rounded-full blur-3xl opacity-20" style={{ background: "#2AA7D4" }} />
                <div className="relative">
                  <div className="font-mono-label text-[11px] tracking-label uppercase text-c-amber">PBL-TDLE</div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-3 mt-5">
                    {["Problem Identification", "Concept Development", "Experimentation", "Verification", "Concept Mastery", "Independent Application", "Original Answers", "Problem-Solving", "Research", "Innovation", "Invention"].map((s, i, arr) => (
                      <span key={s} className="inline-flex items-center gap-3">
                        <span className="font-display text-lg md:text-xl dk-text tracking-tight">{s}</span>
                        {i < arr.length - 1 && <ArrowRight className="w-4 h-4 text-c-teal shrink-0" />}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="mt-10 rounded-2xl border-l-4 border-accent bg-white/5 p-6 md:p-8 max-w-4xl">
              <p className="text-white/80 leading-relaxed">
                PBL-TDLE moves assessment beyond memorised reproduction. It develops students who can <span className="text-white">think independently, construct their own answers, apply concepts, solve unfamiliar problems and create original solutions</span> — preparing them for examinations, professional education, research, entrepreneurship and national development.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* C · Replacing Chalk-and-Talk with Demonstration-Based Learning */}
      <section className="border-t border-white/10" data-testid="lr-demonstration">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <div className="md:sticky md:top-28">
                <SectionLabel n="C">Chapter</SectionLabel>
                <h2 className="font-display text-3xl md:text-4xl text-white mt-6 tracking-tight leading-tight font-light">
                  Replacing chalk-and-talk with <span className="text-accent">demonstration-based learning</span>
                </h2>
                <div className="mt-6 inline-flex items-center gap-2 border border-white/15 rounded-full px-4 py-2">
                  <FlaskConical className="w-4 h-4 text-accent" />
                  <span className="font-mono-label text-[11px] tracking-label uppercase text-white/80">Feature 01 · PBL-TDLE</span>
                </div>
              </div>
            </div>

            <div className="md:col-span-8 md:col-start-5">
              <Reveal>
                <p className="text-white/70 text-lg md:text-xl leading-relaxed mb-6 max-w-2xl">
                  For over a century, science and mathematics classrooms have relied on one method — chalk on a board, a teacher's voice, and students taking notes. It looks like teaching. It rarely delivers the depth of learning that modern education demands.
                </p>
              </Reveal>
              <Reveal delay={0.06}>
                <p className="text-white/70 text-lg md:text-xl leading-relaxed mb-6 max-w-2xl">
                  Before we introduce what replaces it, it is worth being honest about what chalk-and-talk actually costs a student — quietly, day after day, across an entire school career.
                </p>
              </Reveal>

              {/* Drawbacks grid */}
              <div className="font-mono-label text-[11px] tracking-label uppercase text-accent mt-10 mb-5">The Drawbacks of Chalk-and-Talk</div>
              <div className="grid sm:grid-cols-2 gap-4" data-testid="lr-chalk-drawbacks">
                {CHALK_DRAWBACKS.map((k, i) => {
                  const Icon = k.icon;
                  return (
                    <Reveal
                      key={k.t}
                      delay={(i % 2) * 0.05}
                      className="relative border border-white/10 rounded-2xl p-5 hover:border-accent/60 transition-colors overflow-hidden group"
                      data-testid={`lr-chalk-drawback-${i}`}
                    >
                      <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full blur-3xl opacity-15 group-hover:opacity-35 transition-opacity" style={{ backgroundColor: k.hex }} />
                      <div className="relative">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center border" style={{ borderColor: k.hex, backgroundColor: `${k.hex}18` }}>
                            <Icon className="w-4.5 h-4.5" style={{ color: k.hex }} />
                          </div>
                          <span className="font-mono-label text-[10px] tracking-label uppercase" style={{ color: k.hex }}>Drawback · {String(i + 1).padStart(2, "0")}</span>
                        </div>
                        <div className="font-display text-lg text-white mt-4 tracking-tight leading-snug">{k.t}</div>
                        <p className="text-white/65 text-sm leading-relaxed mt-2">{k.d}</p>
                      </div>
                    </Reveal>
                  );
                })}
              </div>

              {/* Closing — bridge to demonstration */}
              <Reveal delay={0.1}>
                <div className="mt-12 rounded-2xl border-l-4 border-accent bg-white/5 p-6 md:p-7 max-w-3xl">
                  <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">Why PBL-TDLE Replaces It</div>
                  <p className="text-white/80 leading-relaxed mt-3">
                    A method that hides learning, moves at one pace and cannot develop scientific skills cannot be the primary way we teach science. PBL-TDLE therefore replaces chalk-and-talk with <span className="text-white">live demonstrations on real apparatus</span>, followed by <span className="text-white">every student performing the experiment themselves</span> — turning listening into investigation, and information into understanding.
                  </p>
                </div>
              </Reveal>

              {/* Vertical short — demonstration in action */}
              <Reveal delay={0.12}>
                <div className="mt-12" data-testid="lr-demo-short">
                  <div className="font-mono-label text-[11px] tracking-label uppercase text-accent mb-5">Watch — Demonstration-Based Learning in Action</div>
                  <div className="w-full max-w-[340px] aspect-[9/16] rounded-2xl overflow-hidden border border-white/10 bg-black">
                    <YouTubeEmbed id="nN1IcQK8ziw" title="Demonstration-based learning in action — THECREATOR Education" short />
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Deep Engagement Creates Deep Learning */}
      <section className="border-t border-white/10" data-testid="lr-deep-engagement">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-6">
            <SectionLabel n="D">The Foundation of Deeper Learning</SectionLabel>
            <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight">
              Engaged minds. Deeper learning. Endless possibilities.
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mt-6">
              True learning begins when students become <span className="text-accent font-semibold">active participants</span> in the learning process — not when they are merely attentive.
            </p>
            <p className="text-white/60 leading-relaxed mt-4">
              THECREATOR PBL-TDLE framework places student engagement at the centre of education. Through problem-based learning, teacher demonstrations, hands-on experiments, discussion, collaboration and progressive problem solving, every student is encouraged to participate, investigate, apply concepts and discover new possibilities.
            </p>
            <p className="text-white/60 leading-relaxed mt-4">
              Passive chalk-and-talk does not assure learning for every student on every concept. When concepts are <span className="text-white">investigated, verified, discussed and applied</span> through continuous experimental engagement, every learner develops each concept effectively — throughout K–12.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-6">
            <div className="aspect-[9/16] max-w-xs sm:max-w-sm mx-auto overflow-hidden rounded-2xl border border-white/10 bg-black shadow-2xl shadow-black/40" data-testid="lr-hero-video">
              <YouTubeEmbed id="Wv-z-ebRp1o" title="Learning Redefined — THECREATOR Education" short />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Every Student Every Concept */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="lr-every-student">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-center">
          <Reveal delay={0.05} className="md:col-span-6 order-2 md:order-1">
            <div className="aspect-video overflow-hidden rounded-2xl border border-white/10 bg-black" data-testid="lr-every-student-video">
              <YouTubeEmbed id="orJf__OTtPs" title="Every student engaged — hands-on experiment learning" />
            </div>
          </Reveal>
          <Reveal className="md:col-span-6 order-1 md:order-2">
            <SectionLabel n="E">When Every Student Is Engaged, Every Student Learns.</SectionLabel>
            <h2 className="font-display text-3xl md:text-4xl text-white mt-6 tracking-tight leading-tight font-light">
              Students learn by performing experiments themselves.
            </h2>
            <p className="text-white/70 leading-relaxed mt-6">
              Unlike passive chalk-and-talk classrooms, every student here works at the experimental table using global-standard scientific apparatus. They make measurements, observe outcomes, ask questions, discuss concepts with peers and teachers, and learn at their own pace.
            </p>
            <p className="text-white/60 leading-relaxed mt-4">
              This deep involvement makes learning highly <span className="text-accent">powerful, meaningful and long-lasting</span>.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              {["Observe", "Investigate", "Measure", "Analyse", "Verify", "Apply"].map((s) => (
                <span key={s} className="font-mono-label text-[11px] tracking-label uppercase text-white/85 border border-white/15 rounded-full px-4 py-2">{s}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Three Foundations — Flow / ZPD / Hard Work */}
      <section className="border-t border-white/10" data-testid="lr-foundations">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="F">The Three Foundations of Deep Learning</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            The science of engagement — Flow, ZPD and the discipline of practice.
          </h2>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {FOUNDATIONS.map((f, i) => {
              const Icon = f.icon;
              const cols = ["text-c-blue", "text-c-amber", "text-c-teal"];
              return (
                <Reveal key={f.t} delay={i * 0.08} className="border border-white/10 rounded-2xl p-8 bg-[#050B14] flex flex-col" data-testid={`lr-foundation-${i}`}>
                  <Icon className={`w-8 h-8 ${cols[i]}`} />
                  <div className={`font-mono-label text-[11px] tracking-label uppercase mt-6 ${cols[i]}`}>{f.tag}</div>
                  <h3 className="font-display text-2xl text-white mt-3 tracking-tight leading-tight">{f.t}</h3>
                  <p className="text-white/65 leading-relaxed mt-4 text-sm flex-1">{f.d}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* How PBL-TDLE Sustains Engagement */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="lr-sustains">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-start">
          <Reveal className="md:col-span-5">
            <SectionLabel n="G">How PBL-TDLE Sustains Engagement</SectionLabel>
            <h2 className="font-display text-3xl md:text-4xl text-white mt-6 tracking-tight leading-tight font-light">
              Eight daily habits that keep every student in the game.
            </h2>
            <p className="text-white/60 leading-relaxed mt-6">
              Engagement is not a mood — it is designed into every hour of the school day.
            </p>
          </Reveal>
          <Reveal delay={0.08} className="md:col-span-7">
            <ul className="grid sm:grid-cols-2 gap-3">
              {ENGAGEMENT_BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-3 border border-white/10 rounded-xl p-5 hover:border-accent transition-colors">
                  <CheckCircle2 className="w-5 h-5 text-c-teal shrink-0 mt-0.5" />
                  <span className="text-white/80 leading-relaxed text-sm">{b}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Textbooks in the PBL-TDLE Framework */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="lr-textbooks">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="H">Textbooks in the PBL-TDLE Framework</SectionLabel>

          <div className="grid md:grid-cols-12 gap-12 mt-12 items-center">
            <Reveal className="md:col-span-6">
              <div className="inline-flex items-center gap-2 border border-white/15 rounded-full px-4 py-2 mb-6">
                <BookOpen className="w-4 h-4 text-accent" />
                <span className="font-mono-label text-[11px] tracking-label uppercase text-white/80">Concept Roadmap · Not a Checklist</span>
              </div>
              <h2 className="font-display text-3xl md:text-5xl text-white tracking-tight leading-tight font-light">
                The textbook is where a concept <span className="text-accent">begins</span> — never where learning ends.
              </h2>
              <p className="text-white/75 leading-relaxed mt-6">
                Science and Mathematics are built entirely on concepts. Every law, principle, theory, equation and formula in a textbook is a concept that explains how nature works. The point of learning them is not to memorise the words — it is to understand them deeply enough to <span className="text-white">apply them to unfamiliar, real-world problems</span>.
              </p>
              <p className="text-white/70 leading-relaxed mt-4">
                Conventional teaching relies on reading, lecturing and chalk-and-talk. Students become familiar with concepts, but familiarity is not mastery. And mastery is exactly what every serious examination — JEE, NEET, Olympiads, AP, international entrance tests — actually measures.
              </p>
            </Reveal>

            <Reveal delay={0.1} className="md:col-span-6">
              <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-white/10 bg-black">
                <img
                  src="https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=1200&auto=format&fit=crop&q=75"
                  alt="Open textbook with pencil and notes — the concept roadmap for a PBL-TDLE lesson"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050B14]/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2 text-white/85">
                  <Compass className="w-4 h-4 text-accent shrink-0" />
                  <span className="font-mono-label text-[10px] tracking-label uppercase">From reading material → to a concept roadmap</span>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Six-idea grid distilled from the framework note */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-16" data-testid="lr-textbook-ideas">
            {TEXTBOOK_IDEAS.map((k, i) => {
              const Icon = k.icon;
              return (
                <Reveal
                  key={k.t}
                  delay={(i % 3) * 0.06}
                  className="relative border border-white/10 rounded-2xl p-6 bg-[#050B14] hover:border-accent/60 transition-colors overflow-hidden group"
                  data-testid={`lr-textbook-idea-${i}`}
                >
                  <div className="absolute -top-8 -right-8 w-28 h-28 rounded-full blur-3xl opacity-25 group-hover:opacity-50 transition-opacity" style={{ backgroundColor: k.hex }} />
                  <div className="relative">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center border" style={{ borderColor: k.hex, backgroundColor: `${k.hex}22` }}>
                        <Icon className="w-5 h-5" style={{ color: k.hex }} />
                      </div>
                      <span className="font-mono-label text-[10px] tracking-label uppercase" style={{ color: k.hex }}>Idea · {String(i + 1).padStart(2, "0")}</span>
                    </div>
                    <div className="font-display text-xl text-white mt-4 tracking-tight leading-snug">{k.t}</div>
                    <p className="text-white/65 text-sm leading-relaxed mt-3">{k.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Featured pull quote — the recommended line */}
          <Reveal delay={0.05} className="mt-16">
            <blockquote className="relative rounded-2xl border-l-4 border-accent bg-[#050B14] border-t border-r border-b border-white/10 p-8 md:p-10 max-w-5xl">
              <div className="absolute -top-4 left-8 font-display text-6xl text-accent leading-none select-none" aria-hidden>&ldquo;</div>
              <p className="font-display text-2xl md:text-3xl lg:text-4xl text-white leading-tight tracking-tight">
                Within the PBL-TDLE framework, the textbook is <span className="text-accent">not treated as a book to be completed</span>. Instead, it serves as the <span className="text-accent">concept roadmap</span> for the entire learning process.
              </p>
              <div className="mt-6 flex items-center gap-3">
                <span className="h-px flex-1 bg-white/15" />
                <span className="font-mono-label text-[11px] tracking-label uppercase text-white/50">THECREATOR Education · PBL-TDLE Framework</span>
              </div>
            </blockquote>
          </Reveal>

          {/* Closing paragraph — the transformation */}
          <p className="text-white/70 leading-relaxed mt-10 max-w-4xl">
            The textbook therefore transforms from a source of information into a <span className="text-white">guide for concept development</span> — enabling every student to build lasting understanding, exceptional problem-solving ability and the confidence to succeed in school examinations, competitive entrance tests, research, innovation and lifelong learning.
          </p>
        </div>
      </section>

      {/* I · A New Role for the Teacher — The Research-Educator */}
      <section className="border-t border-white/10" data-testid="lr-research-educator">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="I">A New Role for the Teacher — The Research-Educator</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            From classroom teacher to <span className="text-accent">teacher-researcher</span>.
          </h2>
          <div className="max-w-3xl space-y-4 mt-6">
            <p className="text-white/70 leading-relaxed">
              Traditional teaching places almost the entire responsibility for classroom activity, student engagement, syllabus completion and learning outcomes on the teacher. However, teachers have limited control over students' attention and receive little reliable evidence of actual learning.
            </p>
            <p className="text-white/70 leading-relaxed">
              Through PBL-TDLE, teachers' efforts are converted into <span className="text-white">visible student engagement, measurable concept development and lasting academic progress</span>. PBL-TDLE creates a structured, evidence-based environment in which students become active participants. Teachers demonstrate concepts and guide experiments, investigations and problem-solving while continuously verifying engagement, concept development and application.
            </p>
            <p className="text-white/70 leading-relaxed">
              PBL-TDLE makes the teacher's role more <span className="text-accent">focused, structured, measurable and academically rewarding</span>.
            </p>
          </div>

          <div className="mt-14 space-y-4">
            {[
              { l: "The teacher is held responsible for assured learning but has almost no control over students' attention and engagement. The entire responsibility for learning outcomes falls on the teacher, creating continuous pressure.", s: "Apparatus and experiments engage students naturally, without requiring continuous effort from the teacher. Once students become actively engaged, meaningful learning is assured. A comparatively small effort from the teacher produces substantial student participation, visible learning and immense professional satisfaction." },
              { l: "The teacher cannot be certain how many students were attentive, who remained focused until the end or how much each student understood. This makes the actual outcome of teaching completely uncertain.", s: "Every student participates in demonstrations, experiments, observations, data recording, calculations, discussions and problem-solving. The teacher can directly observe who is participating, what each student understands and where further guidance is required." },
              { l: "The teacher must remain continuously active — speaking, explaining, maintaining attention and managing the class. Repeating this across several periods becomes physically and mentally exhausting.", s: "The teacher introduces the concept through a demonstration, while students learn by performing the experiment themselves. The teacher monitors, facilitates and guides the process — directing the enormous energy of students towards productive learning instead of exhausting their own energy through continuous explanation." },
              { l: "Teachers spend considerable energy delivering content but cannot determine how much was actually received, understood or retained. Their effort may be lost when students remain passive, become inattentive or quickly forget the content.", s: "Students invest their energy in setting up experiments, making observations, recording data, performing calculations, analysing results and drawing conclusions. The teacher guides them closely during the first few experiments. As students become confident, they take the lead while the teacher observes and supports their high standard of learning." },
              { l: "There is no reliable way to verify engagement, concept development, mastery or application. Completed notes, silent classrooms and content delivery do not prove that learning has occurred.", s: "Learning becomes fully verifiable when students complete observation tables, record measurements, perform calculations, analyse evidence and independently verify concepts. Formative Concept Assessments, presentations and application-based problems provide further evidence of concept development and mastery." },
              { l: "Students frequently forget previous learning because it was based mainly on memorisation. Teachers must repeatedly reteach the same content before beginning new topics or academic years.", s: "Concepts developed through demonstrations and actual experimentation become long-lasting because students learn by doing. When students enter the next class, their concepts remain active — allowing teachers to proceed confidently towards deeper and more advanced concept development without repeatedly starting again." },
              { l: "Lesson planning, resource preparation and syllabus completion depend heavily on the teacher's personal effort. Teachers must search for activities, prepare assessments and manage delays caused by repeated teaching.", s: "The cloud-based CoPE-App provides the complete lesson plan for every unit — including concepts, demonstrations, apparatus, experimental procedures, solutions, assessments and each stage of implementation. Teachers require far less routine preparation and can devote their time to experimentation, investigation and developing themselves as researchers." },
              { l: "Teachers must prepare students separately for school examinations, board examinations and entrance examinations. This creates additional pressure, workload and dependence on memorisation.", s: "Students develop concepts and apply them through problem-solving within their regular classrooms. Since JEE, NEET and other competitive examinations are problem-based, this process prepares them systematically for these examinations. Concept mastery also strengthens board-examination performance by reducing dependence on memorisation and the pressure of recalling prepared answers." },
              { l: "Teachers repeatedly teach familiar textbook content with little opportunity to deepen their own knowledge. Limited access to apparatus and real application can cause their subject knowledge and experimental skills to become stagnant.", s: "In every unit, teachers and students work with apparatus and perform several experiments using complete experimental setups. This develops practical skills, strengthens conceptual understanding and builds the competence required to design, assemble and conduct larger and more advanced experiments in the future." },
              { l: "Teachers receive limited opportunities to conduct investigations, develop research skills, publish papers or contribute to innovation. Professional development is often restricted to occasional training programmes.", s: "The apparatus used in PBL-TDLE experiments is globally benchmarked, high-precision equipment capable of producing research-quality data. Teachers gain opportunities to conduct authentic experiments, analyse data, undertake classroom-based and scientific research, prepare suitable work for publication and develop genuine research experience — progressively growing from classroom teachers into teacher-researchers." },
            ].map((row, i) => (
              <Reveal key={i} delay={(i % 3) * 0.05}>
                <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden" data-testid={`lr-teacher-row-${i + 1}`}>
                  <div className="bg-[#070d18] p-6 md:p-8">
                    <div className="flex items-center gap-3">
                      <span className="font-display text-3xl text-white/25 leading-none">{String(i + 1).padStart(2, "0")}</span>
                      <span className="font-mono-label text-[10px] tracking-label uppercase text-white/45">Teacher in Traditional Learning</span>
                    </div>
                    <p className="text-white/65 text-sm leading-relaxed mt-4">{row.l}</p>
                  </div>
                  <div className="dk-panel p-6 md:p-8">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-c-teal shrink-0" />
                      <span className="font-mono-label text-[10px] tracking-label uppercase text-c-teal">Teacher in the PBL-TDLE Framework</span>
                    </div>
                    <p className="dk-text-soft text-sm leading-relaxed mt-4">{row.s}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* J · Traditional Teaching vs PBL-TDLE (moved from Framework page) */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="lr-compare">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="J">Traditional Teaching vs PBL-TDLE</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl">
            Same subject. Same syllabus. Entirely different classroom.
          </h2>
          <div className="mt-12 border border-white/10 rounded-2xl overflow-hidden">
            <div className="grid grid-cols-12 dk-panel font-mono-label text-[11px] tracking-label uppercase">
              <div className="col-span-4 md:col-span-3 p-4 border-r dk-border dk-text">Dimension</div>
              <div className="col-span-4 md:col-span-4 p-4 border-r dk-border dk-text-muted">Traditional classroom</div>
              <div className="col-span-4 md:col-span-5 p-4 text-c-amber">PBL-TDLE classroom</div>
            </div>
            {[
              { d: "Primary activity",   trad: "Content delivery, note-taking",     pbl: "Problem solving with apparatus" },
              { d: "Concept learning",   trad: "Read and memorise",                  pbl: "Investigate, measure, verify" },
              { d: "Assessment",         trad: "Periodic exams",                     pbl: "Continuous formative, per concept" },
              { d: "Teacher role",       trad: "Explainer",                          pbl: "Demonstrator + research mentor" },
              { d: "Student output",     trad: "Recall answers",                     pbl: "Verified concepts + applied solutions" },
              { d: "Room shape",         trad: "Rows of desks facing a board",       pbl: "Concept Lab with apparatus benches" },
              { d: "Feedback speed",     trad: "Weeks (after tests)",                pbl: "Same lesson (via CoPE-App)" },
            ].map((c, i) => (
              <div key={c.d} className={`grid grid-cols-12 text-sm md:text-base border-t border-white/10 ${i % 2 ? "bg-[#050B14]" : "bg-[#070d18]"}`}>
                <div className="col-span-4 md:col-span-3 p-4 border-r border-white/10 font-mono-label text-[11px] tracking-label uppercase text-white/70">{c.d}</div>
                <div className="col-span-4 md:col-span-4 p-4 border-r border-white/10 text-white/55 leading-relaxed">{c.trad}</div>
                <div className="col-span-4 md:col-span-5 p-4 text-white leading-relaxed">{c.pbl}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing manifesto */}
      <section className="border-t border-white/10 bg-cream text-ink" data-testid="lr-manifesto">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="✦" dark>The Manifesto</SectionLabel>
          <Reveal>
            <blockquote className="font-display text-3xl md:text-5xl lg:text-6xl text-ink leading-[1.1] tracking-tight mt-10 max-w-6xl font-light">
              “Engage to Learn. Learn to Innovate.”
            </blockquote>
            <p className="text-ink/65 text-lg leading-relaxed mt-8 max-w-3xl">
              When every learner participates meaningfully, the possibility of excellence extends to the entire class — not only to a few students considered naturally gifted.
            </p>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
